/**
 * action-rail — ОБЩИЙ kit боковой панели действий для ВСЕХ экранов (board / daily
 * / result / replay …). docs/specs/board-compact-rail.md.
 *
 * owner-directive 2026-05-29: «функции в модуле, обязательные для всех экранов,
 * применяю паттерны». attachActionRail НЕ перерисовывает кнопки (чтобы не ломать
 * board) — он ПРИМЕНЯЕТ ОБЩЕЕ ПОВЕДЕНИЕ к уже существующим в DOM кнопкам действий:
 *   1) вешает autofit на кнопки (mode:'line', uniform, общая дельта ACTION_RAIL_FILL);
 *   2) вешает compact-rail (узкая панель → 2 кнопки ⓘ/☰ + попапы);
 *   3) передаёт в compact-rail скорректированный floor (FS_BUTTON_MIN_PX /
 *      fillRatio) — чтобы ОТОБРАЖАЕМЫЙ delta-масштабированный текст никогда не был
 *      ниже floor читаемости (фикс дельта-рассинхрона триггера).
 *
 * CSS rail-кнопок + компакта — в co-located styles/action-rail.css (импортируется
 * здесь): любой экран, вызвавший kit, автоматически получает стили (один источник,
 * generic-классы .action-rail / .rail-compact / .rail-is-compact → без дрейфа).
 */

import '../styles/action-rail.css';

import { attachAutofit, type AutofitHandle } from './autofit-text.js';
import { attachCompactRail, type CompactRailHandle } from './compact-rail.js';
import { FS_BUTTON_MIN_PX } from './viewport-sizer.js';

/**
 * Дельта заполнения кнопки текстом (0…1) — ЕДИНСТВЕННАЯ ручка «насколько крупно
 * текст заполняет кнопку», ОДНА на весь проект (owner-directive 2026-05-29). Размер
 * считается ОТ КНОПКИ (ширина+высота, autofit mode:'line'); дельта масштабирует
 * пропорцию, чтобы текст не раздувался во всю высоту кнопки на больших экранах.
 * board и daily берут её ОТСЮДА (одно место). Меняем только это число.
 */
export const ACTION_RAIL_FILL = 0.7;

export interface ActionRailOptions {
  /** Панель, на которую вешается compact-класс при сужении. */
  readonly rail: HTMLElement;
  /** Контейнер кнопок действий (autofit меряет лейблы; ему ставится `.action-rail`). */
  readonly actionsContainer: HTMLElement;
  /** Контейнер, куда монтируются 2 кнопки-триггера ⓘ/☰ (compact-host). */
  readonly compactHost: HTMLElement;
  /** Реальные кнопки действий (для autofit + измеряемого триггера компакта). */
  readonly actionButtons: readonly HTMLElement[];
  /**
   * Класс компакта на `rail` (по умолчанию generic `rail-is-compact` из
   * action-rail.css). Передавать обычно не нужно.
   */
  readonly compactClass?: string;
  /** Дельта заполнения текста кнопки (по умолчанию ACTION_RAIL_FILL). */
  readonly fillRatio?: number;
  /** Заголовок ⓘ-попапа. */
  readonly infoTitle: string;
  /** Тело ⓘ-попапа (строится при каждом открытии — живые данные). */
  readonly buildInfoBody: () => HTMLElement;
  /** Тело ☰-попапа (`close` — закрыть попап после действия). */
  readonly buildActionsBody: (close: () => void) => HTMLElement;
}

export interface ActionRailHandle {
  /** Принудительный пересчёт autofit (напр. после смены текста кнопок). */
  refit(): void;
  detach(): void;
}

/**
 * ЕДИНЫЙ ЗАКОН ШРИФТА КНОПОК ДЕЙСТВИЙ (ОДИН источник правды). Group-uniform autofit
 * подписей: mode:'line' (весь лейбл в одну строку), uniform (longest label измеряется →
 * ВСЕ кнопки группы одного размера = min, влезающего в самую длинную подпись), fillRatio
 * (одна ручка «насколько крупно»), capToDesign (потолок = дизайн-размер), floor читаемости
 * FS_BUTTON_MIN_PX. Лейбл НИКОГДА не вылезает за кнопку — он УМЕНЬШАЕТСЯ.
 *
 * ЗАЧЕМ ОТДЕЛЬНО ОТ attachActionRail (owner-directive «общее поведение = модуль, не
 * дублирование»): нужен на surface'ах, где есть кнопки действий, но сворачивание в ⓘ/☰
 * (compact-rail) НЕ нужно — напр. error-карточка доски дня («Повторить»/«Свободная игра»).
 * Полный kit (attachActionRail) вызывает ЭТУ ЖЕ функцию → закон шрифта один.
 *
 * `selector` — CSS-селектор кнопок группы (напр. `.action-rail .btn` для rail-панели,
 * `.daily-card-error .btn` для error-карточки). НЕ навешивает rail-layout класс/CSS —
 * только autofit; раскладку кнопок задаёт сам surface (rail-стек / postament-ряд).
 */
export function attachButtonGroupAutofit(
  root: HTMLElement,
  selector: string,
  fillRatio: number = ACTION_RAIL_FILL,
): AutofitHandle {
  return attachAutofit(root, [
    { selector, mode: 'line', uniform: true, fillRatio, capToDesign: true, minPx: FS_BUTTON_MIN_PX },
  ]);
}

/**
 * Привязать общий action-rail kit к экрану. `root` — корень экрана (наблюдается
 * ResizeObserver'ами autofit + compact-rail). Ставит `.action-rail` на контейнер
 * кнопок (CSS rail-правил), вешает autofit + compact-rail. Возвращает handle с
 * detach() для unmount экрана.
 */
export function attachActionRail(root: HTMLElement, opts: ActionRailOptions): ActionRailHandle {
  const fillRatio = opts.fillRatio ?? ACTION_RAIL_FILL;

  // Generic CSS-hook: контейнер кнопок получает rail-правила action-rail.css.
  opts.actionsContainer.classList.add('action-rail');

  // Autofit подписей кнопок — ЕДИНЫЙ закон шрифта кнопок (attachButtonGroupAutofit, тот
  // же, что и у error-карточки и любого surface'а: line + uniform + fillRatio +
  // capToDesign + floor). ОДИН источник правды — без дубля конфигурации.
  const autofit: AutofitHandle = attachButtonGroupAutofit(root, '.action-rail .btn', fillRatio);

  // Floor триггера компакта = FS_BUTTON_MIN_PX (дизайн-floor лейбла), как в
  // закоммиченном board. Прежний `FS_BUTTON_MIN_PX / fillRatio` (дельта-фикс)
  // отправлял board в компакт на ЧАСТЫХ разрешениях (1024×768) — owner-directive
  // 2026-05-29: «доска НЕ должна дрогнуть, тем более на частом разрешении». Поэтому
  // компакт включается только когда сам лейбл не влезает при floor (реально узкая
  // панель), а не когда delta-масштабированный текст чуть мельче floor.
  const compact: CompactRailHandle = attachCompactRail(root, {
    rail: opts.rail,
    compactHost: opts.compactHost,
    actionButtons: opts.actionButtons,
    compactClass: opts.compactClass ?? 'rail-is-compact',
    floorPx: FS_BUTTON_MIN_PX,
    infoTitle: opts.infoTitle,
    buildInfoBody: opts.buildInfoBody,
    buildActionsBody: opts.buildActionsBody,
  });

  return {
    refit(): void {
      autofit.refit();
    },
    detach(): void {
      autofit.detach();
      compact.detach();
      opts.actionsContainer.classList.remove('action-rail');
    },
  };
}
